import type { EventConfig } from '@/types'
import { DEFAULT_SCHEMA_VERSION } from './constants'
import { generateId } from './utils'
import { inheritAll, customInheritance, extractSettings } from '../../server/lib/eventSettings'

export function createDefaultEvent(): EventConfig {
  const id = generateId()
  return {
    id,
    name: 'New Event',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    schemaVersion: DEFAULT_SCHEMA_VERSION,
    settingsInheritance: inheritAll(),
    capture: {
      enabledModes: ['photo'],
      photo: {
        poseCount: 3,
        delayBeforeFirst: 3,
        delayBetweenPoses: 2,
        reviewTime: 5,
        alsoCreateGif: false,
      },
      gif: {
        frameCount: 10,
        frameDelay: 200,
        quality: 10,
      },
      boomerang: {
        duration: 3,
        speed: 1,
      },
      video: {
        enabled: false,
        maxDuration: 15,
      },
      recordSessionVideo: false,
      finishScreenTimeout: 20,
      dataSaver: {
        enabled: false,
        quality: 0.75,
        maxDimension: 1600,
      },
      attractLoop: {
        enabled: false,
        timeout: 60,
        wakeMethod: 'touch',
        videoAsset: '/video/bg_video.mp4',
        maxMessages: 4,
        intervalSeconds: 2.5,
        motionSensitivity: 50,
        phrases: [
          "Step up and take a photo.",
          "Photos are free. Come on in.",
          "Pick a pose, we handle the rest.",
          "Group shots work best close together.",
          "Press the screen when you are ready.",
          "Two or more people is more fun.",
          "Bring a prop from the table.",
          "Your photo is ready in a minute.",
        ],
      },
    },
    camera: {
      deviceId: '',
      resolution: { width: 1920, height: 1080 },
      frameRate: 30,
      mirrorLiveView: true,
      rotation: 0,
      audioInputDeviceId: '',
    },
    audio: {
      microphoneDeviceId: '',
      outputDeviceId: '',
      microphoneGain: 1.0,
      enabled: true,
      counterSoundEnabled: true,
      shutterSoundEnabled: true,
    },
    screens: {
      welcome: {
        backgroundColor: '#111827',
        backgroundImage: null,
        useCameraBg: true,
        width: 1920,
        height: 1080,
        startButtonImage: {
          enabled: false,
          asset: null,
          opacity: 1.0,
        },
        elements: [
          {
            id: generateId(),
            type: 'text',
            x: 560,
            y: 300,
            width: 800,
            height: 80,
            rotation: 0,
            zIndex: 0,
            opacity: 1,
            content: 'Welcome to the Photo Booth',
            color: '#ffffff',
            fontSize: 48,
            fontFamily: 'sans-serif',
            align: 'center',
          },

        ],
      },
      capture: {
        backgroundColor: '#000000',
        backgroundImage: null,
        useCameraBg: false,
        width: 1920,
        height: 1080,
        elements: [
          {
            id: generateId(),
            type: 'camera',
            x: 360,
            y: 90,
            width: 1200,
            height: 900,
            rotation: 0,
            zIndex: 0,
            opacity: 1,
          },
        ],
      },
      sharing: {
        backgroundColor: '#111827',
        backgroundImage: null,
        useCameraBg: false,
        width: 1920,
        height: 1080,
        elements: [
          {
            id: generateId(),
            type: 'text',
            x: 560,
            y: 200,
            width: 800,
            height: 60,
            rotation: 0,
            zIndex: 0,
            opacity: 1,
            content: 'Share Your Photos',
            color: '#ffffff',
            fontSize: 36,
            fontFamily: 'sans-serif',
            align: 'center',
          },
          {
            id: generateId(),
            type: 'qr',
            x: 860,
            y: 400,
            width: 200,
            height: 200,
            rotation: 0,
            zIndex: 1,
            opacity: 1,
          },
        ],
      },
    },
    printLayout: {
      id: generateId(),
      name: 'Default Landscape 6x4',
      paperSize: '6x4',
      customWidth: 1800,
      customHeight: 1200,
      dpi: 300,
      backgroundColor: '#ffffff',
      backgroundImage: null,
      overlayImage: null,
      slots: [
        {
          id: generateId(),
          type: 'photo',
          x: 80,
          y: 80,
          width: 800,
          height: 533,
          rotation: 0,
          zIndex: 0,
          photoIndex: 0,
        },
        {
          id: generateId(),
          type: 'photo',
          x: 920,
          y: 80,
          width: 800,
          height: 533,
          rotation: 0,
          zIndex: 0,
          photoIndex: 1,
        },
        {
          id: generateId(),
          type: 'photo',
          x: 80,
          y: 653,
          width: 800,
          height: 533,
          rotation: 0,
          zIndex: 0,
          photoIndex: 2,
        },
      ],
    },
    effects: {
      digitalProps: false,
      beautyFilter: { enabled: false, strength: 0.5 },
      watermark: { enabled: false, asset: null, position: 'bottom-right', opacity: 0.5 },
      greenScreen: { enabled: false, backgroundAsset: null, threshold: 120 },
      luts: [],
      disclaimer: null,
    },
    virtualAttendant: {
      enabled: false,
      startScreenVideo: null,
      countdownVideo: null,
      beforeCountdownAudio: [],
      afterCaptureAudio: [],
      randomizeBeforeCountdown: false,
    },
    sharing: {
      qrEnabled: true,
      galleryEnabled: true,
      emailEnabled: false,
      emailSmtp: null,
      smsEnabled: false,
    },
    kiosk: {
      enabled: true,
      publicAccess: 'disabled',
      publicAccessDuration: 'permanent',
      publicAccessDurationValue: 1,
      publicAccessExpiresAt: null,
      publicAccessCreatedAt: null,
      publicToken: null,
      sortOrder: 'newestFirst',
      contentFilter: 'all',
      interval: 10,
      showEventName: true,
      showQrCode: true,
      autoRefreshInterval: 10,
      refreshOnNewPhoto: true,
      blurBackground: true,
      transitionEffect: 'fade',
      loopSlideshow: true,
      backgroundColor: '#000000',
    },
    gallerySettings: {
      enabled: true,
      publicAccess: 'disabled',
      publicAccessDuration: 'permanent',
      publicAccessDurationValue: 1,
      publicAccessExpiresAt: null,
      publicAccessCreatedAt: null,
      publicToken: null,
      allowDownloads: true,
      allowZipDownload: true,
      showSurveyResults: false,
      showCaptureTime: true,
      galleryTheme: 'dark',
      gridColumns: 3,
      watermarkPreview: false,
      sortBy: 'oldestFirst',
      groupBySession: true,
      showVideos: false,
      allowBulkDownload: false,
      includeVideosInZip: false,
    },
    survey: null,
    triggers: [],
    branding: {
      primaryColor: '#3b82f6',
      accentColor: '#f59e0b',
      logoAsset: null,
      backgroundAsset: null,
      fontFamily: 'sans-serif',
    },
  }
}

export function createPartyTemplate(): EventConfig {
  const event = createDefaultEvent()
  event.name = 'Party Template'
  event.branding.primaryColor = '#ec4899'
  event.branding.accentColor = '#8b5cf6'
  event.capture.enabledModes = ['photo', 'gif']
  event.settingsInheritance = customInheritance(extractSettings(event))
  event.screens.welcome.backgroundColor = '#2e1065'
  return event
}

export function createMinimalTemplate(): EventConfig {
  const event = createDefaultEvent()
  event.name = 'Minimal Template'
  event.branding.primaryColor = '#000000'
  event.branding.accentColor = '#ffffff'
  event.screens.welcome.elements = []
  event.printLayout.slots = [event.printLayout.slots[0]]
  return event
}
